/**
 * AutoPauseCampaignWhenThresholdHitPage
 *
 * Threshold-driven auto-pause control surface. Two panes:
 *   1) Rules builder — local-state list of (campaign_id, metric, limit, window,
 *      enabled) entries with live "currently-breaching?" preview computed via
 *      the same utilisation math the BE runs. Submitting a rule posts one
 *      evaluation to /api/v1/auto-pause-campaign-when-threshold-hit which
 *      decides pause/warn/skip and persists non-skip outcomes (idempotent on
 *      (tenant, event_id)).
 *   2) Recent-pause feed — last 100 ledger rows from GET /, filterable by
 *      campaign_id and action, plus a summary tile (pause/warn counts).
 *
 * Rules live in component state only — they are inputs to evaluations, not a
 * persisted entity. Executing a rule calls POST which produces the ledger
 * entry that shows up in the feed.
 *
 * Styling: green-only accents (green-600/700/50), zinc neutrals. No gradients.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	predictedAction,
	predictedSeverity,
	utilization,
	type PauseAction,
	type ThresholdType,
} from "./api";
import {
	useAutoPauseEvaluate,
	useAutoPauseList,
	useAutoPauseSummary,
} from "./hooks";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20 disabled:cursor-not-allowed disabled:bg-zinc-50";
const SELECT = INPUT;
const BTN_PRIMARY =
	"rounded-[6px] border border-green-700 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:border-zinc-300 disabled:bg-zinc-200 disabled:text-zinc-500";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const LABEL = "text-xs font-medium text-zinc-700";

const THRESHOLD_OPTIONS: ThresholdType[] = [
	"spend",
	"cap",
	"conversions",
	"ftd_count",
	"clicks",
	"custom",
];
const WINDOW_OPTIONS = ["5m", "15m", "1h", "6h", "24h"] as const;
type WindowOption = (typeof WINDOW_OPTIONS)[number];

interface Rule {
	id: string;
	campaign_id: string;
	threshold_type: ThresholdType;
	threshold_limit: number;
	observed_value: number;
	window: WindowOption;
	enabled: boolean;
}

function makeRuleId(): string {
	if (
		typeof crypto !== "undefined" &&
		typeof crypto.randomUUID === "function"
	) {
		return crypto.randomUUID();
	}
	return `rule-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function formatTs(ts: number): string {
	if (!Number.isFinite(ts) || ts <= 0) return "--";
	const d = new Date(ts * 1000);
	return d.toISOString().replace("T", " ").slice(0, 19);
}

function actionChip(action: PauseAction): {
	label: string;
	className: string;
	testId: string;
} {
	if (action === "pause") {
		return {
			label: "PAUSE",
			className:
				"inline-flex items-center rounded-[4px] border border-green-700 bg-green-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white",
			testId: "chip-pause",
		};
	}
	if (action === "warn") {
		return {
			label: "WARN",
			className:
				"inline-flex items-center rounded-[4px] border border-green-600 bg-green-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-green-800",
			testId: "chip-warn",
		};
	}
	return {
		label: "SKIP",
		className:
			"inline-flex items-center rounded-[4px] border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-600",
		testId: "chip-skip",
	};
}

export function AutoPauseCampaignWhenThresholdHitPage() {
	const [rules, setRules] = useState<Rule[]>([
		{
			id: makeRuleId(),
			campaign_id: "",
			threshold_type: "spend",
			threshold_limit: 100,
			observed_value: 0,
			window: "1h",
			enabled: true,
		},
	]);

	const [campaignFilter, setCampaignFilter] = useState("");
	const [actionFilter, setActionFilter] = useState<
		"all" | "pause" | "warn"
	>("all");

	const listParams = useMemo(
		() => ({
			campaign_id: campaignFilter.trim() || undefined,
			action: actionFilter === "all" ? undefined : actionFilter,
		}),
		[campaignFilter, actionFilter],
	);

	const listQuery = useAutoPauseList(listParams);
	const summaryQuery = useAutoPauseSummary();
	const evalMut = useAutoPauseEvaluate();

	const updateRule = (id: string, patch: Partial<Rule>) => {
		setRules((prev) =>
			prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
		);
	};

	const removeRule = (id: string) => {
		setRules((prev) => prev.filter((r) => r.id !== id));
	};

	const addRule = () => {
		setRules((prev) => [
			...prev,
			{
				id: makeRuleId(),
				campaign_id: "",
				threshold_type: "spend",
				threshold_limit: 100,
				observed_value: 0,
				window: "1h",
				enabled: true,
			},
		]);
	};

	const handleEvaluate = (rule: Rule, e: FormEvent) => {
		e.preventDefault();
		if (!rule.enabled) return;
		if (!rule.campaign_id.trim()) return;
		if (!Number.isFinite(rule.threshold_limit) || rule.threshold_limit <= 0)
			return;
		evalMut.mutate({
			event_id: `rule-${rule.id}-${Date.now()}`,
			campaign_id: rule.campaign_id.trim(),
			threshold_type: rule.threshold_type,
			threshold_limit: rule.threshold_limit,
			observed_value: rule.observed_value,
			reason: `manual rule eval window=${rule.window}`,
		});
	};

	const items = listQuery.data?.items ?? [];
	const summary = summaryQuery.data;
	const pauseCount =
		summary?.by_action.find((b) => b.action === "pause")?.count ?? 0;
	const warnCount =
		summary?.by_action.find((b) => b.action === "warn")?.count ?? 0;

	return (
		<main
			data-testid="auto-pause-campaign-when-threshold-hit-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-green-700">
					auto-pause-campaign-when-threshold-hit
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					Auto-pause when threshold hit
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Define per-campaign threshold rules (metric, limit, window).
					Evaluations compare observed value against the limit using the
					same math as the backend, then post the decision to the ledger
					when it exceeds the warn floor.
				</p>
			</header>

			<section
				className="grid grid-cols-1 gap-4 sm:grid-cols-3"
				aria-label="Ledger summary"
			>
				<div className={CARD} data-testid="summary-total">
					<p className={LABEL}>Total decisions persisted</p>
					<p className="mt-1 text-2xl font-semibold text-zinc-900">
						{summaryQuery.isLoading ? "--" : (summary?.total ?? 0)}
					</p>
				</div>
				<div className={CARD} data-testid="summary-pause">
					<p className={LABEL}>Pause</p>
					<p className="mt-1 text-2xl font-semibold text-green-700">
						{summaryQuery.isLoading ? "--" : pauseCount}
					</p>
				</div>
				<div className={CARD} data-testid="summary-warn">
					<p className={LABEL}>Warn</p>
					<p className="mt-1 text-2xl font-semibold text-green-600">
						{summaryQuery.isLoading ? "--" : warnCount}
					</p>
				</div>
			</section>

			<section className={CARD} aria-label="Auto-pause rules">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Rules
					</h2>
					<button
						type="button"
						className={BTN_GHOST}
						onClick={addRule}
						data-testid="add-rule"
					>
						+ Add rule
					</button>
				</div>
				{rules.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="rules-empty"
					>
						No rules configured.
					</p>
				) : (
					<ul
						className="mt-4 flex flex-col gap-4"
						data-testid="rules-list"
					>
						{rules.map((rule) => {
							const util = utilization(
								rule.observed_value,
								rule.threshold_limit,
							);
							const predAction = predictedAction(
								rule.observed_value,
								rule.threshold_limit,
							);
							const predSev = predictedSeverity(
								rule.observed_value,
								rule.threshold_limit,
							);
							const chip = actionChip(predAction);
							const canSubmit =
								rule.enabled &&
								rule.campaign_id.trim().length > 0 &&
								Number.isFinite(rule.threshold_limit) &&
								rule.threshold_limit > 0;
							return (
								<li
									key={rule.id}
									className="rounded-[8px] border border-zinc-200 p-4"
									data-testid={`rule-${rule.id}`}
								>
									<form
										onSubmit={(e) => handleEvaluate(rule, e)}
										className="flex flex-col gap-3"
									>
										<div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
											<div className="flex flex-col gap-1">
												<label
													className={LABEL}
													htmlFor={`campaign-${rule.id}`}
												>
													Campaign ID
												</label>
												<input
													id={`campaign-${rule.id}`}
													type="text"
													className={INPUT}
													value={rule.campaign_id}
													onChange={(e) =>
														updateRule(rule.id, {
															campaign_id:
																e.currentTarget.value,
														})
													}
													disabled={!rule.enabled}
													placeholder="camp_abc"
													data-testid={`rule-${rule.id}-campaign`}
												/>
											</div>
											<div className="flex flex-col gap-1">
												<label
													className={LABEL}
													htmlFor={`metric-${rule.id}`}
												>
													Metric
												</label>
												<select
													id={`metric-${rule.id}`}
													className={SELECT}
													value={rule.threshold_type}
													onChange={(e) =>
														updateRule(rule.id, {
															threshold_type: e
																.currentTarget
																.value as ThresholdType,
														})
													}
													disabled={!rule.enabled}
													data-testid={`rule-${rule.id}-metric`}
												>
													{THRESHOLD_OPTIONS.map((t) => (
														<option key={t} value={t}>
															{t}
														</option>
													))}
												</select>
											</div>
											<div className="flex flex-col gap-1">
												<label
													className={LABEL}
													htmlFor={`limit-${rule.id}`}
												>
													Threshold
												</label>
												<input
													id={`limit-${rule.id}`}
													type="number"
													min={0}
													step="any"
													className={INPUT}
													value={rule.threshold_limit}
													onChange={(e) =>
														updateRule(rule.id, {
															threshold_limit: Number(
																e.currentTarget.value,
															),
														})
													}
													disabled={!rule.enabled}
													data-testid={`rule-${rule.id}-limit`}
												/>
											</div>
											<div className="flex flex-col gap-1">
												<label
													className={LABEL}
													htmlFor={`observed-${rule.id}`}
												>
													Observed
												</label>
												<input
													id={`observed-${rule.id}`}
													type="number"
													min={0}
													step="any"
													className={INPUT}
													value={rule.observed_value}
													onChange={(e) =>
														updateRule(rule.id, {
															observed_value: Number(
																e.currentTarget.value,
															),
														})
													}
													disabled={!rule.enabled}
													data-testid={`rule-${rule.id}-observed`}
												/>
											</div>
											<div className="flex flex-col gap-1">
												<label
													className={LABEL}
													htmlFor={`window-${rule.id}`}
												>
													Window
												</label>
												<select
													id={`window-${rule.id}`}
													className={SELECT}
													value={rule.window}
													onChange={(e) =>
														updateRule(rule.id, {
															window: e
																.currentTarget
																.value as WindowOption,
														})
													}
													disabled={!rule.enabled}
													data-testid={`rule-${rule.id}-window`}
												>
													{WINDOW_OPTIONS.map((w) => (
														<option key={w} value={w}>
															{w}
														</option>
													))}
												</select>
											</div>
										</div>

										<div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-100 pt-3">
											<div className="flex items-center gap-3 text-sm">
												<label
													className="flex items-center gap-2"
													data-testid={`rule-${rule.id}-enabled-label`}
												>
													<input
														type="checkbox"
														className="h-4 w-4 rounded border-zinc-300 text-green-600 focus:ring-green-600/30"
														checked={rule.enabled}
														onChange={(e) =>
															updateRule(rule.id, {
																enabled:
																	e.currentTarget
																		.checked,
															})
														}
														data-testid={`rule-${rule.id}-toggle`}
													/>
													<span className="font-medium text-zinc-800">
														Enabled
													</span>
												</label>
												<span
													className="text-xs text-zinc-500"
													data-testid={`rule-${rule.id}-util`}
												>
													utilization:{" "}
													{(util * 100).toFixed(0)}%
												</span>
												<span
													className={chip.className}
													data-testid={`rule-${rule.id}-chip`}
												>
													{chip.label}
												</span>
												<span
													className="text-xs text-zinc-500"
													data-testid={`rule-${rule.id}-sev`}
												>
													sev: {predSev}
												</span>
											</div>
											<div className="flex items-center gap-2">
												<button
													type="button"
													onClick={() => removeRule(rule.id)}
													className={BTN_GHOST}
													data-testid={`rule-${rule.id}-remove`}
												>
													Remove
												</button>
												<button
													type="submit"
													className={BTN_PRIMARY}
													disabled={
														!canSubmit || evalMut.isPending
													}
													data-testid={`rule-${rule.id}-submit`}
												>
													{evalMut.isPending
														? "Evaluating."
														: "Evaluate now"}
												</button>
											</div>
										</div>
									</form>
								</li>
							);
						})}
					</ul>
				)}
				{evalMut.error ? (
					<p
						className="mt-3 text-xs text-red-600"
						role="alert"
						data-testid="eval-error"
					>
						{evalMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Recent pause ledger">
				<div className="flex flex-wrap items-end justify-between gap-3">
					<h2 className="text-base font-semibold text-zinc-900">
						Recent decisions
					</h2>
					<div className="flex flex-wrap items-end gap-2">
						<div className="flex flex-col gap-1">
							<label
								className={LABEL}
								htmlFor="filter-campaign"
							>
								Campaign
							</label>
							<input
								id="filter-campaign"
								type="text"
								className={INPUT}
								value={campaignFilter}
								onChange={(e) =>
									setCampaignFilter(e.currentTarget.value)
								}
								placeholder="camp_abc"
								data-testid="filter-campaign"
							/>
						</div>
						<div className="flex flex-col gap-1">
							<label className={LABEL} htmlFor="filter-action">
								Action
							</label>
							<select
								id="filter-action"
								className={SELECT}
								value={actionFilter}
								onChange={(e) =>
									setActionFilter(
										e.currentTarget.value as
											| "all"
											| "pause"
											| "warn",
									)
								}
								data-testid="filter-action"
							>
								<option value="all">all</option>
								<option value="pause">pause</option>
								<option value="warn">warn</option>
							</select>
						</div>
					</div>
				</div>

				<div className="mt-4">
					{listQuery.isLoading ? (
						<p
							className="text-sm text-zinc-500"
							data-testid="auto-pause-campaign-when-threshold-hit-loading"
						>
							Loading.
						</p>
					) : listQuery.error ? (
						<p
							className="text-sm text-red-600"
							role="alert"
							data-testid="list-error"
						>
							{listQuery.error.message}
						</p>
					) : items.length === 0 ? (
						<p
							className="text-sm text-zinc-500"
							data-testid="auto-pause-campaign-when-threshold-hit-empty"
						>
							No decisions yet.
						</p>
					) : (
						<div className="overflow-x-auto">
							<table
								className="min-w-full divide-y divide-zinc-200 text-sm"
								data-testid="auto-pause-campaign-when-threshold-hit-list"
							>
								<thead className="text-left text-xs uppercase tracking-wider text-zinc-500">
									<tr>
										<th className="py-2 pr-4">When</th>
										<th className="py-2 pr-4">Campaign</th>
										<th className="py-2 pr-4">Metric</th>
										<th className="py-2 pr-4">Observed</th>
										<th className="py-2 pr-4">Limit</th>
										<th className="py-2 pr-4">Action</th>
										<th className="py-2 pr-4">Severity</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-zinc-100">
									{items.map((row) => {
										const chip = actionChip(row.action);
										return (
											<tr
												key={row.id}
												data-testid={`auto-pause-campaign-when-threshold-hit-row-${row.id}`}
											>
												<td className="py-2 pr-4 font-mono text-xs text-zinc-600">
													{formatTs(row.created_at)}
												</td>
												<td className="py-2 pr-4 text-zinc-800">
													{row.campaign_id}
												</td>
												<td className="py-2 pr-4 text-zinc-700">
													{row.threshold_type}
												</td>
												<td className="py-2 pr-4 font-mono text-zinc-800">
													{row.observed_value}
												</td>
												<td className="py-2 pr-4 font-mono text-zinc-800">
													{row.threshold_limit}
												</td>
												<td className="py-2 pr-4">
													<span
														className={chip.className}
													>
														{chip.label}
													</span>
												</td>
												<td className="py-2 pr-4 text-zinc-700">
													{row.severity}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</section>
		</main>
	);
}

export default AutoPauseCampaignWhenThresholdHitPage;
